import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { VISEMES, type VisemeId } from './visemes.js';

/**
 * Asesor dibujado (version generica, sin el rostro del Dr.).
 *
 * Un personaje ilustrado que no se parece a nadie en concreto: un asesor joven
 * con saco azul y corbata dorada del CEC. Todo se mueve en vivo, sin videos:
 *
 *   - la boca sigue la voz con los mismos visemas que la sincronizacion labial,
 *     y se abre mas o menos con el volumen real;
 *   - parpadea, mira a los lados y levanta las cejas al empezar cada frase;
 *   - la cabeza se balancea, asiente al hablar y se ladea al escuchar;
 *   - al pensar mira hacia arriba y aparece una burbuja con puntos;
 *   - al presentarse, saluda con la mano.
 *
 * El movimiento se aplica directamente sobre el SVG en cada fotograma (sin
 * pasar por React): solo la forma de la boca viene de las props.
 */

export type CartoonStatus = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

interface Props {
  status: CartoonStatus;
  viseme: VisemeId;
  ampRef: MutableRefObject<number>;
  /** Cambia cada vez que el asesor debe saludar con la mano (al presentarse). */
  waveSignal?: number;
}

const SKIN = '#E0A77E';
const SKIN_SHADE = '#C98B63';
const SKIN_LINE = '#A5684A';
const HAIR = '#2E211B';
const HAIR_HI = '#4A362C';
const LIP = '#9E5448';

export function CartoonAdvisor({ status, viseme, ampRef, waveSignal = 0 }: Props) {
  const headRef = useRef<SVGGElement>(null);
  const bodyRef = useRef<SVGGElement>(null);
  const jawRef = useRef<SVGGElement>(null);
  const eyesRef = useRef<SVGGElement[]>([]);
  const pupilsRef = useRef<SVGGElement[]>([]);
  const browsRef = useRef<SVGGElement[]>([]);
  const armRef = useRef<SVGGElement>(null);
  const bubbleRef = useRef<SVGGElement>(null);
  const dotsRef = useRef<SVGCircleElement[]>([]);
  const statusRef = useRef(status);
  statusRef.current = status;
  const visemeRef = useRef(viseme);
  const waveRef = useRef(waveSignal);
  const svgRef = useRef<SVGSVGElement>(null);
  // Encuadre: en escenarios altos se ve el dibujo entero (recortando los
  // lados); en los bajos y anchos (movil, panel del widget) se encuadra la
  // cabeza entera y los hombros, con el fondo extendido a los lados.
  const [viewBox, setViewBox] = useState('0 0 400 480');
  useEffect(() => {
    const el = svgRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height) return;
      const aspect = width / height;
      if (aspect <= 400 / 400) return setViewBox('0 0 400 480');
      const top = 58;
      const h = 312; // del pelo a los hombros
      const w = h * aspect;
      setViewBox(`${(200 - w / 2).toFixed(1)} ${top} ${w.toFixed(1)} ${h}`);
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let last = performance.now();
    // Estado del movimiento
    let blink = 0;
    let nextBlink = 1.5 + Math.random() * 2.5;
    let blinkT = -1;
    let gx = 0;
    let gy = 0;
    let gxT = 0;
    let gyT = 0;
    let nextGaze = 1 + Math.random() * 2;
    let brow = 0;
    let browPulse = 0;
    let nod = 0;
    let nodT = 1;
    let tilt = 0;
    let lookX = 0;
    let lookY = 0;
    let env = 0;
    let jaw = 0;
    let think = 0;
    let wave = 0;
    let waveT = 99;
    let lastViseme = visemeRef.current;
    let lastWave = waveRef.current;
    let prevStatus = statusRef.current;
    const phases = Array.from({ length: 4 }, () => Math.random() * 10);

    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = now / 1000;
      const st = statusRef.current;
      const speaking = st === 'speaking';
      const listening = st === 'listening';
      const thinking = st === 'thinking' || st === 'transcribing';
      const amp = speaking ? Math.min(1, ampRef.current) : 0;
      env += (amp - env) * (1 - Math.exp(-dt / 0.1));

      // --- Gestos por evento -------------------------------------------------
      const v = visemeRef.current;
      if (speaking && v !== lastViseme && lastViseme === 'REST' && v !== 'REST' && nodT > 0.9) {
        // Arranque de frase: cejas arriba y un asentimiento breve.
        browPulse = 1;
        nodT = 0;
      }
      lastViseme = v;
      if (waveRef.current !== lastWave) {
        lastWave = waveRef.current;
        waveT = 0;
        browPulse = 1;
      }
      if (st !== prevStatus) {
        if (listening) nodT = 0;
        prevStatus = st;
      }
      if (listening && nodT > 1.8 + Math.random()) nodT = 0; // "te sigo"

      // --- Parpadeo ----------------------------------------------------------
      nextBlink -= dt;
      if (nextBlink <= 0 && blinkT < 0) {
        blinkT = 0;
        nextBlink = 2.2 + Math.random() * 3.6;
        if (Math.random() < 0.18) nextBlink = 0.25; // parpadeo doble
      }
      if (blinkT >= 0) {
        blinkT += dt;
        const u = blinkT / 0.16;
        blink = u < 0.4 ? u / 0.4 : Math.max(0, 1 - (u - 0.4) / 0.6);
        if (u >= 1) {
          blink = 0;
          blinkT = -1;
        }
      }

      // --- Mirada --------------------------------------------------------------
      nextGaze -= dt;
      if (thinking) {
        gxT = 5;
        gyT = -6;
      } else if (listening || speaking) {
        if (nextGaze <= 0) {
          gxT = Math.random() < 0.7 ? 0 : (Math.random() * 2 - 1) * 4;
          gyT = 0;
          nextGaze = 1.2 + Math.random() * 2.5;
        }
      } else if (nextGaze <= 0) {
        gxT = Math.random() < 0.45 ? 0 : (Math.random() * 2 - 1) * 6;
        gyT = (Math.random() * 2 - 1) * 2.5;
        nextGaze = 1.4 + Math.random() * 3;
      }
      // Sacadas: la mirada salta rapido, no se desliza.
      gx += (gxT - gx) * (1 - Math.exp(-dt / 0.05));
      gy += (gyT - gy) * (1 - Math.exp(-dt / 0.05));

      // --- Cejas, cabeza, boca --------------------------------------------------
      browPulse = Math.max(0, browPulse - dt / 0.6);
      const browT = thinking ? 0.7 : listening ? 0.55 : Math.max(browPulse, env > 0.75 ? 0.4 : 0);
      brow += (browT - brow) * (1 - Math.exp(-dt / 0.12));

      nodT += dt;
      const nodAmp = listening ? 5 : 3.5;
      nod = nodT < 0.55 ? Math.sin((Math.PI * nodT) / 0.55) * nodAmp : 0;
      const tiltT = listening ? 5 : thinking ? -4 : 0;
      tilt += (tiltT - tilt) * (1 - Math.exp(-dt / 0.35));
      const lookXT = thinking ? 8 : gxT * 0.8;
      const lookYT = thinking ? -6 : 0;
      lookX += (lookXT - lookX) * (1 - Math.exp(-dt / 0.35));
      lookY += (lookYT - lookY) * (1 - Math.exp(-dt / 0.35));

      const open = VISEMES[v]?.open ?? 0;
      const jawT = speaking ? open * (0.55 + 0.45 * env) * 7 : 0;
      jaw += (jawT - jaw) * (1 - Math.exp(-dt / 0.045));

      think += ((thinking ? 1 : 0) - think) * (1 - Math.exp(-dt / 0.2));
      waveT += dt;
      wave = waveT < 2.4 ? Math.min(1, waveT / 0.35, (2.4 - waveT) / 0.4) : 0;

      // --- Aplicar ------------------------------------------------------------
      const k = reduced ? 0 : 1;
      const swayX = k * (Math.sin(t * 0.45 + phases[0]!) * 3 + Math.sin(t * 0.83 + phases[1]!) * 1.5) * (speaking ? 1.4 : 1);
      const swayY = k * Math.sin(t * 0.61 + phases[2]!) * 1.6;
      const swayR = k * (Math.sin(t * 0.37 + phases[3]!) * 1.4 + env * Math.sin(t * 5.1) * 1.2);
      headRef.current?.setAttribute(
        'transform',
        `translate(${(swayX + lookX).toFixed(2)} ${(swayY + nod + lookY * 0.3).toFixed(2)}) rotate(${(swayR + tilt).toFixed(2)} 200 330)`,
      );
      const breath = 1 + k * 0.008 * Math.sin((2 * Math.PI * t) / 4.2);
      bodyRef.current?.setAttribute('transform', `translate(200 480) scale(${breath.toFixed(4)}) translate(-200 -480) translate(${(swayX * 0.35).toFixed(2)} 0)`);
      jawRef.current?.setAttribute('transform', `translate(0 ${jaw.toFixed(2)})`);
      eyesRef.current.forEach((e) => e?.setAttribute('transform', `translate(0 ${(206 * blink * 0.92).toFixed(2)}) scale(1 ${(1 - blink * 0.92).toFixed(3)})`));
      pupilsRef.current.forEach((p) => p?.setAttribute('transform', `translate(${gx.toFixed(2)} ${gy.toFixed(2)})`));
      browsRef.current.forEach((b, i) => {
        // Pensando, una ceja mas alta que la otra.
        const extra = thinking && i === 1 ? 3 : 0;
        b?.setAttribute('transform', `translate(0 ${(-brow * 7 - extra).toFixed(2)})`);
      });
      if (armRef.current) {
        const up = 1 - wave;
        const swing = wave > 0 ? Math.sin(waveT * 11) * 16 : 0;
        armRef.current.setAttribute('transform', `translate(0 ${(up * 230).toFixed(1)}) rotate(${swing.toFixed(2)} 318 372)`);
      }
      if (bubbleRef.current) bubbleRef.current.setAttribute('opacity', think.toFixed(3));
      dotsRef.current.forEach((d, i) => d?.setAttribute('opacity', (0.35 + 0.65 * Math.max(0, Math.sin(t * 5 - i * 0.9))).toFixed(2)));
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [ampRef]);

  visemeRef.current = viseme;
  waveRef.current = waveSignal;
  const shape = VISEMES[viseme] ?? VISEMES.REST;

  return (
    <svg ref={svgRef} className="cartoon" viewBox={viewBox} preserveAspectRatio="xMidYMid slice" role="img" aria-label="Asesor virtual (ilustración)">
      <defs>
        <linearGradient id="ct-bg" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
          <stop offset="0" stopColor="#dfe6ff" />
          <stop offset="1" stopColor="#aebcff" />
        </linearGradient>
        <radialGradient id="ct-halo" cx="50%" cy="40%" r="50%">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ct-suit" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2a3a9c" />
          <stop offset="1" stopColor="#1a2466" />
        </linearGradient>
        <radialGradient id="ct-face" cx="45%" cy="38%" r="70%">
          <stop offset="0" stopColor="#EDBB93" />
          <stop offset="1" stopColor={SKIN} />
        </radialGradient>
        <clipPath id="ct-eye-l">
          <ellipse cx="164" cy="206" rx="17" ry="19" />
        </clipPath>
        <clipPath id="ct-eye-r">
          <ellipse cx="236" cy="206" rx="17" ry="19" />
        </clipPath>
      </defs>

      {/* Fondo: degradado suave con formas que sugieren el campus */}
      <rect x="-800" y="0" width="2000" height="480" fill="url(#ct-bg)" />
      <circle cx="200" cy="200" r="190" fill="url(#ct-halo)" />
      <circle cx="58" cy="92" r="26" fill="#ffffff" opacity="0.35" />
      <circle cx="352" cy="150" r="16" fill="#ffffff" opacity="0.3" />
      <rect x="318" y="46" width="44" height="44" rx="12" fill="#ffcc00" opacity="0.22" transform="rotate(14 340 68)" />
      <path d="M26,300 l22,-10 l22,10 l-22,10 z" fill="#2c3cf0" opacity="0.14" />

      {/* Cuerpo (respira) */}
      <g ref={bodyRef}>
        <path d="M200,352 C130,352 62,380 40,430 L30,480 L370,480 L360,430 C338,380 270,352 200,352 Z" fill="url(#ct-suit)" />
        {/* camisa y corbata dorada del CEC */}
        <path d="M168,354 L200,414 L232,354 L218,346 L200,372 L182,346 Z" fill="#eef3ff" />
        <path d="M200,376 L212,392 L206,440 L200,448 L194,440 L188,392 Z" fill="#ffcc00" />
        <path d="M200,376 L212,392 L200,398 L188,392 Z" fill="#e6b400" />
        {/* solapas */}
        <path d="M168,354 L200,416 L178,480 L120,480 L140,390 Z" fill="#1c2878" opacity="0.9" />
        <path d="M232,354 L200,416 L222,480 L280,480 L260,390 Z" fill="#1c2878" opacity="0.75" />
        {/* insignia */}
        <rect x="262" y="410" width="34" height="20" rx="4" fill="#ffffff" opacity="0.92" />
        <text x="279" y="424.5" textAnchor="middle" fontSize="10" fontWeight="800" fill="#000099" fontFamily="Arial, sans-serif">CEC</text>
      </g>

      {/* Cabeza y cuello (se mueven juntos) */}
      <g ref={headRef}>
        <path d="M176,296 L224,296 L230,362 Q200,374 170,362 Z" fill={SKIN_SHADE} />
        {/* orejas */}
        <ellipse cx="110" cy="222" rx="16" ry="24" fill={SKIN} />
        <ellipse cx="290" cy="222" rx="16" ry="24" fill={SKIN} />
        <path d="M112,210 Q104,222 112,236" fill="none" stroke={SKIN_LINE} strokeWidth="3" strokeLinecap="round" opacity="0.5" />
        <path d="M288,210 Q296,222 288,236" fill="none" stroke={SKIN_LINE} strokeWidth="3" strokeLinecap="round" opacity="0.5" />
        {/* cara */}
        <path d="M112,196 C112,132 150,104 200,104 C250,104 288,132 288,196 C288,262 258,318 200,320 C142,318 112,262 112,196 Z" fill="url(#ct-face)" />
        {/* mejillas */}
        <ellipse cx="140" cy="252" rx="17" ry="10" fill="#ff7f7f" opacity="0.22" />
        <ellipse cx="260" cy="252" rx="17" ry="10" fill="#ff7f7f" opacity="0.22" />
        {/* pelo */}
        <path
          d="M108,214 C96,142 136,82 206,80 C266,78 304,118 294,200 C290,174 278,152 256,140 C236,152 204,156 176,142 C152,154 128,176 118,216 Z"
          fill={HAIR}
        />
        <path d="M176,142 C188,118 222,102 262,112 C240,112 214,122 198,142 Z" fill={HAIR_HI} />
        <path d="M130,150 C142,124 164,108 190,100" fill="none" stroke={HAIR_HI} strokeWidth="4" strokeLinecap="round" opacity="0.7" />
        {/* cejas */}
        <g ref={(el) => { if (el) browsRef.current[0] = el; }}>
          <path d="M144,176 Q164,164 186,172 L185,180 Q164,173 146,184 Z" fill={HAIR} />
        </g>
        <g ref={(el) => { if (el) browsRef.current[1] = el; }}>
          <path d="M256,176 Q236,164 214,172 L215,180 Q236,173 254,184 Z" fill={HAIR} />
        </g>
        {/* ojos */}
        {([['l', 164], ['r', 236]] as const).map(([id, cx], i) => (
          <g key={id} ref={(el) => { if (el) eyesRef.current[i] = el; }}>
            <ellipse cx={cx} cy="206" rx="17" ry="19" fill="#ffffff" />
            <g clipPath={`url(#ct-eye-${id})`}>
              <g ref={(el) => { if (el) pupilsRef.current[i] = el; }}>
                <circle cx={cx} cy="208" r="11" fill="#5A3A24" />
                <circle cx={cx} cy="208" r="6" fill="#1A120E" />
                <circle cx={cx + 4} cy="203" r="3.4" fill="#ffffff" />
                <circle cx={cx - 3} cy="212" r="1.6" fill="#ffffff" opacity="0.8" />
              </g>
            </g>
            <path d={`M${cx - 18},200 Q${cx},184 ${cx + 18},200`} fill="none" stroke={SKIN_LINE} strokeWidth="2.6" strokeLinecap="round" opacity="0.55" />
          </g>
        ))}
        {/* nariz */}
        <path d="M200,218 Q193,236 196,242 Q202,247 209,241" fill="none" stroke={SKIN_LINE} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
        {/* boca: visema + mandibula */}
        <g ref={jawRef}>
          <path d={shape.lip} fill="#5A2430" />
          {shape.teeth ? <path d={shape.teeth} fill="#ffffff" /> : null}
          {shape.tongue ? <path d={shape.tongue} fill="#D9707E" /> : null}
          <path d={shape.lip} fill="none" stroke={LIP} strokeWidth="3.4" strokeLinejoin="round" />
        </g>
        {/* hoyuelos de la sonrisa */}
        <path d="M162,254 Q160,260 164,265" fill="none" stroke={SKIN_LINE} strokeWidth="2" strokeLinecap="round" opacity="0.35" />
        <path d="M238,254 Q240,260 236,265" fill="none" stroke={SKIN_LINE} strokeWidth="2" strokeLinecap="round" opacity="0.35" />
      </g>

      {/* Mano que saluda (aparece al presentarse) */}
      <g ref={armRef} transform="translate(0 230)">
        <path d="M290,480 L300,388 Q318,370 336,388 L350,480 Z" fill="url(#ct-suit)" />
        <rect x="302" y="372" width="32" height="14" rx="5" fill="#eef3ff" />
        <g>
          <rect x="304" y="318" width="30" height="58" rx="14" fill={SKIN} />
          <rect x="300" y="300" width="9" height="36" rx="4.5" fill={SKIN} />
          <rect x="310" y="292" width="9" height="40" rx="4.5" fill={SKIN} />
          <rect x="320" y="294" width="9" height="38" rx="4.5" fill={SKIN} />
          <rect x="330" y="302" width="8.5" height="32" rx="4.25" fill={SKIN} />
          <rect x="288" y="336" width="24" height="10" rx="5" fill={SKIN} transform="rotate(-35 300 341)" />
        </g>
      </g>

      {/* Burbuja de pensamiento */}
      <g ref={bubbleRef} opacity="0">
        <circle cx="300" cy="118" r="6" fill="#ffffff" />
        <circle cx="318" cy="96" r="9" fill="#ffffff" />
        <rect x="316" y="36" width="70" height="42" rx="21" fill="#ffffff" />
        {[338, 351, 364].map((x, i) => (
          <circle key={x} ref={(el) => { if (el) dotsRef.current[i] = el; }} cx={x} cy="57" r="4.5" fill="#2c3cf0" />
        ))}
      </g>
    </svg>
  );
}
